import type { ZoneBuilder } from '../types';
import { scatterPeople } from '../crowd';
import { scatterForest } from '../forest';
import { hillLat } from '../hills';
import { RIO_COLONIAL, RIO_JUNGLE, RIO_SAND, RIO_SEA, UMBRELLA_COLORS, darken } from '../palettes';
import { hash } from '../water';

/**
 * scenery/zones/rio.ts — bright tropical noon. The Copacabana beachfront
 * (wave promenade, sand, turquoise sea, parasols) → Lapa's painted colonial
 * houses under the aqueduct → the deep Tijuca rainforest below Christ →
 * a palm boulevard to the Maracanã. The sea lies on side +1.
 */

export const RIO_SEA_SIDE = 1;

const copacabana: ZoneBuilder = ({ rng, peopleRng, hw, at, s, a, side, step }) => {
  if (side === RIO_SEA_SIDE) {
    // The famous black-and-white wave promenade hugging the road…
    {
      const [x, y, z] = at(hw + 3.2);
      a.promenade.push({ x, y, z, rotY: s.dir, w: 4.4, d: step + 0.6, color: 0xffffff });
    }
    // …then the sand, the parasols, and the turquoise Atlantic.
    //
    // rio-ts round — THE SAND TRAIN, reshaped. The old single 26-wide slab ran
    // its hard rectangular edge out past the GS1 water's shoreline: each slab's
    // top rides THIS step's ground while the sea surface follows every row's
    // own smoothed ground +0.13, so on any grade the slab's far corners poked
    // through the surface and the waterline read as a sawtooth of rectangle
    // corners from high angles (the GS1 flag). Now the DRY sheet stops at
    // hw+25.8 — a hair inside the water's innermost jittered shoreline
    // (shore 32 − beach tuck 3.5 − jitter 2.2 = hw+26.3) — so it can never
    // cross the waterline, and two stepped-down WET SHELVES plus a deep apron
    // carry the sand under the sea past the outermost shoreline (hw+30.7),
    // out to hw+34.6. The visible boundary is the water's own organic foam
    // edge BY CONSTRUCTION; a grade-forced shelf poke reads as broken shoals
    // (short 15u treads, index-hash jittered edges + yaw), never a staircase.
    // Deterministic: the jitter is hash(step index) — the rng stream carries
    // ZERO draws in this block, exactly as before. Umbrella/people/kiosk
    // placement below is untouched (byte-identical, fingerprint-checked).
    // Drops are sized against the MEASURED beach grade (5.16% max over the
    // first third, rio seed): a flat tread of depth d on grade g pokes when
    // g·d/2 > 0.035 + drop. The 13-deep A shelf at 0.22 and the 26.4-deep
    // B shelf at 0.42 / apron at 0.85 stay under the (grade-following) sea
    // sheet on all but the worst stretch, where tips break ≤0.07 (A) and
    // ≤0.23 (B, at its underwater outer reach) — shoals, not a staircase.
    {
      const k = Math.round(s.distance / step);
      // rio-nits (R3): THE DRY SHEET NOW REACHES THE SHORELINE'S WANDER BAND.
      // The GS1 pass sized the dry sheet against the shoreline's INNERMOST
      // reach (shore 32 − beach tuck 3.5 − jitter 2.2 = hw+26.3) and stopped
      // a hair inside it at hw+25.8. But that jitter is two-sided: the GS1
      // grid's shoreline column (buildRunGrid `inner`, the first vertex that
      // rides at ground+0.13) wanders OUT to hw+30.7, and between the dry
      // sheet's fixed edge and that column the sea surface is still ramping
      // up from its buried land skirt (ground−2.0, 2.5u inboard) — so the wet
      // shelf underneath is left standing in the open air. Measured on seed
      // 123: shelf A is exposed above the true waterline on EVERY row, by
      // 0.9-4.25u (median 2.2). That strip is what showed the reviewer's
      // "grey sliver" at arc ~0.30: sand[221]'s down-course END FACE (a
      // 0.05-tall slab side, buildFlat lift 0.07 / thinY 0.05) standing
      // 0.105 proud of sand[225] where the beach grade crests and reverses —
      // an inherent flat-slab-train riser that is invisible everywhere else
      // BECAUSE it is underwater. So: widen the dry sheet to hw+28.4 ± 0.6
      // (hash-jittered, like the shelves' outer edges) and carry shelf A's
      // inner edge out with it. The sheet can never poke through the sea by
      // construction — its top rides ground+0.095 while every near-band water
      // column rides ground+0.13 — and the jittered edge keeps any
      // grade-forced tip reading as a shoal, never a row of aligned corners.
      // MEASURED (seed 123, 58 water rows): the exposed shelf-A strip drops
      // median 2.16 → 0.00, rows over 0.3u 55 → 17, worst 4.26 → 2.03, and
      // the reviewer's sliver row (arc 0.30) is fully covered. Going further
      // out was rejected: at hw+29.5+ the sheet clears the shoreline's INNER
      // roll (hw+26.3) by >3u on the tightest rows, which is the reach that
      // brought back the GS1 sawtooth.
      const dryOut = hw + 28.4 + 1.2 * (hash(k * 13 + 7) - 0.5);
      const dryIn = hw + 5.5;
      const [x, y, z] = at((dryIn + dryOut) / 2);
      a.sand.push({ x, y, z, rotY: s.dir, w: dryOut - dryIn, d: step * 2.2, color: RIO_SAND });
      // Wet shelf A: dips 0.22 under the dry sheet, feathered outer edge.
      // rio-nits (R3): NO yaw jitter here any more — the ±0.05 rad yaw made
      // adjacent rows' 13-deep treads CRISSCROSS on straight stretches, and
      // where the shoreline foam locally retreats the higher tread's d-edge
      // floated 0.05 proud over its neighbour as a dark "plank" on dry sand
      // (measured at arc ~0.30: sand[221] over sand[217], relative yaw
      // 0.046). The organic shoal read lives in the hash-jittered OUTER
      // EDGE widths (kept); parallel treads can only meet in road-parallel
      // terrace seams, which the sand grain already owns.
      const aOut = hw + 29.2 + 1.8 * (hash(k * 3 + 1) - 0.5);
      // Inner edge rides out with the dry sheet, staying tucked under even its
      // narrowest roll (hw+27.8) so no gap can ever open between the two.
      const aIn = hw + 26.0;
      const [ax1, , az1] = at((aIn + aOut) / 2);
      a.sand.push({
        x: ax1,
        y: y - 0.22,
        z: az1,
        rotY: s.dir,
        w: aOut - aIn,
        d: 13,
        color: RIO_SAND,
      });
      // Wet shelf B: 0.42 down, reaches past the outermost shoreline reach.
      // This is the WELDED mid-layer: full step*2.2 overlap and no yaw jitter,
      // because the feathered A shelves above it fan V-gaps open on bends
      // (measured: green ground squares at the waterline every step) — B must
      // be un-gappable so any A gap shows wet sand, never ground.
      const bOut = hw + 31.6 + 1.6 * (hash(k * 7 + 3) - 0.5);
      const bIn = hw + 24.6;
      const [bx1, , bz1] = at((bIn + bOut) / 2);
      a.sand.push({
        x: bx1,
        y: y - 0.42,
        z: bz1,
        rotY: s.dir,
        w: bOut - bIn,
        d: step * 2.2,
        color: RIO_SAND,
      });
      // Deep apron: the guaranteed lowest layer — full step*2.2 overlap so it
      // can never gap on a curve, buried well under the sea surface, tucked
      // out to hw+34.6 so no camera ever sights ground at the waterline.
      const [cx1, , cz1] = at(hw + 32.1);
      a.sand.push({ x: cx1, y: y - 0.85, z: cz1, rotY: s.dir, w: 5.0, d: step * 2.2, color: RIO_SAND });
    }
    {
      const w = rng.range(55, 70);
      const [x, y, z] = at(hw + 32 + w / 2);
      a.ponds.push({ x, y, z, rotY: s.dir, w, d: step * 2.2, color: RIO_SEA, arc: s.distance, side, shore: hw + 32 });
    }
    if (rng.next() < 0.5) {
      const [x, y, z] = at(hw + 8 + rng.range(0, 18));
      a.umbrellas.push({ x, y: y + 0.07, z, rotY: 0, s: rng.range(0.9, 1.3), color: rng.pick(UMBRELLA_COLORS) });
    }
    if (rng.next() < 0.4) {
      const [x, y, z] = at(hw + 4.4);
      a.palms.push({ x, y, z, rotY: rng.range(0, Math.PI * 2), s: rng.range(0.9, 1.25) });
    }
  } else {
    // The white hotel wall of Avenida Atlântica.
    if (rng.next() < 0.85) {
      const w = rng.range(10, 15);
      const h = rng.range(14, 26);
      const lat = hw + 2.5 + w / 2 + rng.range(0, 3);
      const [x, y, z] = at(lat);
      const color = rng.pick([0xf2efe6, 0xe8e4d4, 0xf6f2ea, 0xdfd8c8]);
      a.buildings.push({ x, y, z, rotY: s.dir, w, h, d: rng.range(10, 14), color });
      a.plain.push({ x, y: y + h - 0.4, z, rotY: s.dir, w: w + 0.8, h: 0.9, d: 12, color: darken(color, 0.8) });
    }
    if (rng.next() < 0.3) {
      const [x, y, z] = at(hw + 2.2);
      a.palms.push({ x, y, z, rotY: rng.range(0, Math.PI * 2), s: rng.range(0.9, 1.2) });
    }
  }
  // Copacabana beach: sunbathers + bathers out on the sand; strollers kerbside.
  if (side === RIO_SEA_SIDE)
    scatterPeople({ peopleRng, at, a }, { band: [hw + 7, hw + 27], chance: 0.85, count: 4, sit: 0.5 });
  else scatterPeople({ peopleRng, at, a }, { band: [hw + 1.6, hw + 4], chance: 0.4, count: 2 });
};

const lapa: ZoneBuilder = ({ rng, peopleRng, hw, at, s, a, progress, marks }) => {
  // Keep the road clear right at the aqueduct so the arches own the moment.
  const archAt = marks.arcosDaLapa;
  if (archAt != null && Math.abs(progress - archAt) < 0.016) return;
  // Painted colonial terraces, shoulder to shoulder.
  let placedTerrace = false;
  if (rng.next() < 0.9) {
    placedTerrace = true;
    const w = rng.range(7, 11);
    const h = rng.range(6, 12);
    const d = rng.range(8, 12);
    const lat = hw + 2 + w / 2 + rng.range(0, 3);
    const [x, y, z] = at(lat);
    const color = rng.pick(RIO_COLONIAL);
    a.buildings.push({ x, y, z, rotY: s.dir, w, h, d, color });
    a.plain.push({ x, y: y + h - 0.4, z, rotY: s.dir, w: w + 0.8, h: 0.9, d: d + 0.8, color: 0xf2efe6 });
    // The Lapa signatures (M14): TERRACOTTA hip roofs over the white cornice,
    // tall white-framed windows with juliet rails, and an arched doorway —
    // the painted-colonial look that makes the district.
    a.roofs.push({ x, y: y + h + 0.4, z, rotY: s.dir, w: w + 1.6, h: 1.7, d: d + 1.6, color: rng.pick([0xb45a36, 0xa84e2e, 0xbe6440]) });
    // rio-ts round — the dressing rebuilt at true joinery proportions. The old
    // pass floated ONE oversized white plate (2.2×2.6, 0.2 proud) + a proud
    // dark board on each terrace; at kerb distance they read as blank
    // billboards and a dark crucifix patch (QA rio finding 8). Now: recessed
    // dark PANES behind slim white border BARS (window units either side of
    // the door line), a juliet rail under each sill, and the doorway as a
    // near-flush dark opening in a white surround. All geometry derives from
    // already-drawn rng values (w/h/d/colour) — zero new draws, and every
    // push still lands in a.plain, so the layout stream is byte-identical.
    const face = lat - w / 2; // the road-facing facade plane
    const along = Math.sin(s.dir);
    const alongZ = Math.cos(s.dir);
    const wy = y + h * 0.55; // window-row centre (same height the plate wore)
    const slab = (
      latC: number,
      u: number,
      base: number,
      bw: number,
      bh: number,
      bd: number,
      c: number,
    ): void => {
      const [px, , pz] = at(latC);
      a.plain.push({ x: px + along * u, y: base, z: pz + alongZ * u, rotY: s.dir, w: bw, h: bh, d: bd, color: c });
    };
    const windowUnit = (u: number): void => {
      // rio-nits: the pane wears the facade's windowGlass slate — at kerb
      // distance the original 0x23262e read as a flat BLACK VOID punched in
      // the wall; this reads as unlit-but-glassy sky-toned glazing.
      // shared-2a: 0x46566c -> 0x7a8496. That number was chosen to sit level
      // with the PAINTED facade cells back when they were '#3a4658'; the
      // shared pass then lifted those to '#6e7684' (builders' rio style) and
      // this flat, unmultiplied slab became the darkest thing on the whole
      // terrace — the void read came back on a lit 3D pane instead of a
      // canvas one. Held one step under the facade's own frame cream so the
      // glazing still reads as glass, not as another white bar.
      slab(face + 0.02, u, wy - 0.95, 0.12, 1.9, 0.95, 0x7a8496); // glazed pane
      slab(face + 0.05, u - 0.555, wy - 0.95, 0.14, 1.9, 0.16, 0xf6f2ea); // border bars
      slab(face + 0.05, u + 0.555, wy - 0.95, 0.14, 1.9, 0.16, 0xf6f2ea);
      slab(face + 0.05, u, wy + 0.95, 0.14, 0.16, 1.27, 0xf6f2ea);
      slab(face + 0.05, u, wy - 1.11, 0.14, 0.16, 1.27, 0xf6f2ea);
      slab(face + 0.1, u, wy - 1.0, 0.16, 0.45, 1.15, 0x2a2d34); // juliet rail
    };
    windowUnit(d * 0.22);
    if (d >= 10) windowUnit(-d * 0.22);
    // The arched doorway: a near-flush dark leaf in a white surround.
    slab(face + 0.02, 0, y, 0.12, 2.9, 1.5, darken(color, 0.45));
    slab(face + 0.05, -0.84, y, 0.14, 2.9, 0.18, 0xf6f2ea);
    slab(face + 0.05, 0.84, y, 0.14, 2.9, 0.18, 0xf6f2ea);
    slab(face + 0.05, 0, y + 2.9, 0.14, 0.18, 1.86, 0xf6f2ea);
  }
  if (rng.next() < 0.18) {
    // rio-nits (palm-through-facade, ~0.42): the kerb palm stands at
    // hw+2.4 while the terrace facades START at hw+2..5 — a crown at
    // 5.5-7.7 high with ~2.2·s frond spread (+ the curved trunk's random
    // bow) pierces any terrace on its step, worst where two colours meet
    // (measured: fronds through the red AND purple facades). The draws
    // still burn (stream byte-identical downstream); the palm only PLANTS
    // on the ~10% terrace-gap steps, where it owns the gap instead of
    // growing through a house. Lean trees never enter lapa (no LEAN_CONFIG
    // entry), so this block is the only palm source here.
    const rotY = rng.range(0, Math.PI * 2);
    const scale = rng.range(0.8, 1.1);
    if (!placedTerrace) {
      const [x, y, z] = at(hw + 2.4);
      a.palms.push({ x, y, z, rotY, s: scale });
    }
  }
  scatterPeople({ peopleRng, at, a }, { band: [hw + 1.6, hw + 4.5], chance: 0.5, count: 3 });
};

const tijuca: ZoneBuilder = ({ rng, hw, at, s, a, step }) => {
  // Deep rainforest: layered jungle greens, dense canopies, steep hills.
  {
    const w = rng.range(50, 80);
    const [x, y, z] = at(hw + w / 2 + 1);
    a.grass.push({ x, y, z, rotY: s.dir, w, d: step * 2.2, color: rng.pick([0x1d6a30, 0x226e34, 0x195c2a]) });
  }
  // M67 (owner): deep rainforest you can SEE THROUGH — cascaded jungle trees
  // with gaps, front row pulled back + small so nothing overhangs the road or
  // curtains the ball (it used to stack trees up to 2× size right at the rail).
  scatterForest(rng, hw, at, a, {
    kind: 'tree',
    colors: RIO_JUNGLE,
    count: [3, 6],
    inner: 6,
    depth: 44,
    gap: 8,
    sizeNear: 0.85,
    sizeFar: 1.9,
  });
  if (rng.next() < 0.3) {
    const [x, y, z] = at(hw + 3 + rng.range(0, 30));
    a.palms.push({ x, y, z, rotY: rng.range(0, Math.PI * 2), s: rng.range(0.9, 1.4) });
  }
  if (rng.next() < 0.34) {
    // M98: jungle hills keep their edge off the road (same draws, clamped).
    // rio-ts: these RIO_JUNGLE-coloured domes get the canopy-clump treatment
    // (vertex noise + tint map) in builders.buildHills — the palette IS the
    // routing key, so keep hill colours on RIO_JUNGLE only. The QA "scribble
    // streak" read on the old smooth faces was half-buried forest canopies +
    // the pre-GS palm plank fronds crossing them; the clump texture folds the
    // buried blobs into the canopy read (positions untouched).
    const base = hw + 18 + rng.range(0, 40);
    const r = rng.range(22, 46);
    const [x, y, z] = at(hillLat(hw, base, r));
    a.hills.push({ x, y, z, r, h: rng.range(8, 22), color: rng.pick(RIO_JUNGLE) });
  }
};

const maracana: ZoneBuilder = ({ rng, peopleRng, hw, at, s, a }) => {
  // The approach boulevard: palms in rhythm, low blocks behind.
  if (rng.next() < 0.55) {
    const [x, y, z] = at(hw + 2.6);
    a.palms.push({ x, y, z, rotY: rng.range(0, Math.PI * 2), s: rng.range(1.0, 1.3) });
  }
  if (rng.next() < 0.4) {
    const w = rng.range(10, 16);
    const h = rng.range(7, 14);
    const [x, y, z] = at(hw + 8 + w / 2 + rng.range(0, 6));
    a.buildings.push({ x, y, z, rotY: s.dir, w, h, d: rng.range(10, 14), color: rng.pick(RIO_COLONIAL) });
  }
  // Match-day crowds streaming toward the stadium.
  scatterPeople({ peopleRng, at, a }, { band: [hw + 1.8, hw + 9], chance: 0.8, count: 4 });
};

export const RIO_ZONES = { copacabana, lapa, tijuca, maracana };
